import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Text,
  useToast,
} from '@chakra-ui/react';
import { customerUsersApi } from '@texas/api/endpoints/customerUsersApi';
import { UserType } from '@texas/api/endpoints/userApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { BodyOverride } from '@texas/components/shared/BodyOverride';
import { useSignalSubscriptionEvents } from '@texas/components/shared/hooks/useSignalSubscriptionEvents';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { TexasHeader } from '@texas/components/shared/TexasHeader';
import { UserAvatar } from '@texas/components/shared/users/UserAvatar';
import { useActiveSession } from '@texas/hooks/useSession';
import { compareEnum } from '@texas/utils/helpers/enumHelpers';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { customerUserEvents } from './events';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { SelectUserModal } from './SelectUserModal';
import { WarningComponent } from '@texas/components/shared/WarningComponent';
import { UserRoleRow } from './UserRoleRow';
import { AddUser } from './AddUser';
import { IconUtilButtons } from './IconUtilButtons';
import { getUserTypeLabel } from './shared';
import { SetCustomTypeModal } from './SetCustomTypeModal';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import {
  TeamMatrixBranchHeader,
  TeamMatrixCorner,
  TeamMatrixGrid,
} from '@texas/components/shared/users/TeamMatrix';

interface Props {
  customerId: number;
}

export function CustomerUsers({ customerId }: Props) {
  const { t } = useTranslation();
  const toast = useToast();

  const { data, refetch, loading } = useApiResource(customerUsersApi.getUsers);
  const { isOpen, onClose, value, onOpen } = useValueDisclosure<{
    branchId?: number;
    userType: UserType | null;
  }>();
  const {
    isOpen: isOpenCustomType,
    onClose: onCloseCustomType,
    value: valueCustomType,
    onOpen: onOpenCustomType,
  } = useValueDisclosure<{
    customerUserId: number;
    customType: string;
  }>();

  useEffect(() => {
    refetch(customerId);
  }, [customerId, refetch]);

  const session = useActiveSession();

  useSignalSubscriptionEvents({
    triggerOnEvent: () => {
      refetch(customerId);
    },
    signalEvents: [
      customerUserEvents.customerUserAdded,
      customerUserEvents.customerUserRemoved,
      customerUserEvents.onCustomerUserSetMain,
      customerUserEvents.onCustomerUserCustomTypeChanged,
    ],
  });

  const { request: addReq, loading: addLoading } = useApiRequest(
    customerUsersApi.addUser,
  );

  const performAdd = (
    userId: number,
    branchId: number | undefined,
    type: UserType | null,
  ) =>
    request(
      addReq,
      [customerId, { userId, branchId, type }],
      (id) => {
        customerUserEvents.customerUserAdded.dispatch();
        if (type === null) {
          onOpenCustomType({ customerUserId: id, customType: '' });
        }
      },
      (error) => {
        toast({
          title: t('general.actionFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );

  const bcUsers = useMemo(() => {
    return (
      data
        ?.filter((x) =>
          compareEnum(UserType, x.userType, UserType.BrandCoordinator),
        )
        .sort((a, b) => Number(b.isMain) - Number(a.isMain)) ?? []
    );
  }, [data]);

  const kamUsers = useMemo(() => {
    return (
      data
        ?.filter((x) =>
          compareEnum(UserType, x.userType, UserType.KeyAccountManager),
        )
        .sort((a, b) => Number(b.isMain) - Number(a.isMain)) ?? []
    );
  }, [data]);

  const unAllocUsers = useMemo(() => {
    return (
      data
        ?.filter((x) => x.userType === null && x.branchId === null)
        .sort((a, b) => Number(b.isMain) - Number(a.isMain)) ?? []
    );
  }, [data]);

  return (
    <BodyOverride>
      <Flex align="center" pb={4} justify="space-between">
        <TexasHeader text={t('customerUser.brandInternalRoles')} />
        <Button
          variant="texas-solid"
          leftIcon={<Icons.FilePdfBox boxSize={defaultIconSize} />}
          as="a"
          href={customerUsersApi.downloadTeamPdf(customerId)}
          target="_blank"
          download={true}
          aria-label={t('customerUser.exportTeam')}
        >
          {t('customerUser.exportTeam')}
        </Button>
      </Flex>

      <Flex gap={4} direction="column">
        {(loading || addLoading) && (
          <SpinnerWithLabel label={t('general.loadingData')} />
        )}
        <Card variant="dark">
          <CardBody p={4}>
            <Flex gap={8}>
              <Box role="group">
                <Box>
                  <WarningComponent
                    warning={
                      bcUsers.length === 0 ? t('customerUser.noBc') : undefined
                    }
                  >
                    <Text variant="header-medium">
                      {t('user.userType.brandCoordinator')}
                    </Text>
                  </WarningComponent>
                </Box>

                <Flex gap={4} pt={1} flexWrap="wrap">
                  {bcUsers.map((x) => {
                    return (
                      <UserAvatar
                        key={x.userName}
                        name={x.userName}
                        email={x.email}
                        label={getUserTypeLabel(
                          x.userType,
                          x.customUserType,
                          t,
                        )}
                        showTeams={true}
                        showMainBadge={x.isMain}
                        size="sm"
                        buttons={
                          <IconUtilButtons
                            isMain={x.isMain}
                            customerUserId={x.id}
                          />
                        }
                      />
                    );
                  })}
                  <AddUser
                    onClick={() =>
                      onOpen({ userType: UserType.BrandCoordinator })
                    }
                    title={t('customerUser.addUser')}
                    sub={t('general.clickToAdd')}
                    size="sm"
                  />
                </Flex>
              </Box>

              <Box role="group">
                <Box>
                  <WarningComponent
                    warning={
                      kamUsers.length === 0
                        ? t('customerUser.noKam')
                        : undefined
                    }
                  >
                    <Text variant="header-medium">
                      {t('user.userType.keyAccountManager')}
                    </Text>
                  </WarningComponent>
                </Box>

                <Flex gap={4} pt={1} flexWrap="wrap">
                  {kamUsers.map((x) => {
                    return (
                      <UserAvatar
                        key={x.userName}
                        name={x.userName}
                        email={x.email}
                        label={getUserTypeLabel(
                          x.userType,
                          x.customUserType,
                          t,
                        )}
                        showTeams={true}
                        showMainBadge={x.isMain}
                        size="sm"
                        buttons={
                          <IconUtilButtons
                            isMain={x.isMain}
                            customerUserId={x.id}
                          />
                        }
                      />
                    );
                  })}
                  <AddUser
                    onClick={() =>
                      onOpen({ userType: UserType.KeyAccountManager })
                    }
                    title={t('customerUser.addUser')}
                    sub={t('general.clickToAdd')}
                    size="sm"
                  />
                </Flex>
              </Box>
            </Flex>
          </CardBody>
        </Card>

        <Card variant="dark">
          <CardBody p={4}>
            <Box role="group">
              <Box>
                <Text variant="header-medium">{t('customerUser.other')}</Text>
              </Box>

              <Flex gap={4} pt={1} flexWrap="wrap">
                {unAllocUsers.map((x) => {
                  return (
                    <UserAvatar
                      key={x.userName}
                      name={x.userName}
                      email={x.email}
                      label={getUserTypeLabel(x.userType, x.customUserType, t)}
                      showTeams={true}
                      size="sm"
                      buttons={
                        <IconUtilButtons
                          isMain={x.isMain}
                          customerUserId={x.id}
                          onChangeCustomTypeClick={() =>
                            onOpenCustomType({
                              customerUserId: x.id,
                              customType: x.customUserType ?? '',
                            })
                          }
                        />
                      }
                    />
                  );
                })}
                <AddUser
                  onClick={() => onOpen({ userType: null })}
                  title={t('customerUser.addUser')}
                  sub={t('general.clickToAdd')}
                  size="sm"
                />
              </Flex>
            </Box>
          </CardBody>
        </Card>

        <Card variant="dark">
          <CardBody p={4}>
            <TeamMatrixGrid branchCount={session.branches.length}>
              <TeamMatrixCorner />
              {session.branches.map((x) => {
                return <TeamMatrixBranchHeader key={x.id} branch={x} />;
              })}
              <UserRoleRow
                title={t('user.userType.productDeveloper')}
                userType={UserType.ProductDeveloper}
                rowIndex={2}
                users={data ?? []}
                onAddClick={(branchId) =>
                  onOpen({ branchId, userType: UserType.ProductDeveloper })
                }
              />
              <UserRoleRow
                title={t('user.userType.customerService')}
                userType={UserType.CustomerService}
                rowIndex={3}
                users={data ?? []}
                onAddClick={(branchId) =>
                  onOpen({ branchId, userType: UserType.CustomerService })
                }
              />
              <UserRoleRow
                title={t('customerUser.other')}
                userType={null}
                rowIndex={4}
                users={data ?? []}
                onAddClick={(branchId) => onOpen({ branchId, userType: null })}
                onChangeCustomTypeClick={(id, type) =>
                  onOpenCustomType({ customerUserId: id, customType: type })
                }
              />
            </TeamMatrixGrid>
          </CardBody>
        </Card>
      </Flex>
      {value && (
        <SelectUserModal
          customerId={customerId}
          branchId={value.branchId}
          userType={value.userType}
          filterIds={
            data
              ?.filter(
                (x) =>
                  x.branchId == value.branchId &&
                  compareEnum(UserType, x.userType, value.userType),
              )
              .map((x) => x.userId) ?? []
          }
          isOpen={isOpen}
          onClose={onClose}
          onSelect={(id) => {
            performAdd(id, value.branchId, value.userType);
            onClose();
          }}
        />
      )}
      {valueCustomType && (
        <SetCustomTypeModal
          customerUserId={valueCustomType.customerUserId}
          customType={valueCustomType.customType}
          onUpdate={() => {
            customerUserEvents.onCustomerUserCustomTypeChanged.dispatch();
            onCloseCustomType();
          }}
          isOpen={isOpenCustomType}
          onClose={onCloseCustomType}
        />
      )}
    </BodyOverride>
  );
}
